<template>
  <div class="inv">

    <!-- Hero -->
    <header class="inv__hero">
      <div class="inv__hero-bg"></div>
      <div class="inv__hero-glow"></div>
      <div class="inv__hero-inner">
        <div class="inv__hero-badge">
          <span class="inv__hero-badge-dot"><span class="inv__hero-badge-ping"></span></span>
          SHC Signal · {{ $t('home.pricingLabel') }}
        </div>
        <h1 class="inv__hero-title">{{ $t('invest.heroTitle') }}</h1>
        <p class="inv__hero-desc">{{ $t('invest.heroDesc') }}</p>
      </div>
      <div class="inv__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z" fill="#f2f0ea"/>
        </svg>
      </div>
    </header>

    <!-- For Whom -->
    <section class="inv__section inv__section--light">
      <div class="inv__shell">
        <div class="inv__section-header">
          <h2 class="inv__section-title">{{ $t('invest.forWhomTitle') }}</h2>
          <div class="inv__divider"></div>
        </div>
        <div class="inv__cards">
          <div
            v-for="(card, i) in forWhomCards"
            :key="i"
            class="inv__card"
            :class="{ 'inv__card--accent': i === 3 }"
            :data-index="String(i + 1).padStart(2, '0')"
          >
            <div class="inv__card-icon" :class="{ 'inv__card-icon--accent': i === 3 }">
              <i :class="card.icon"></i>
            </div>
            <h3 class="inv__card-title">{{ $t(card.titleKey) }}</h3>
            <p class="inv__card-desc">{{ $t(card.descKey) }}</p>
            <span class="inv__card-tag" :class="{ 'inv__card-tag--accent': i === 3 }">{{ $t(card.tagKey) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="inv__section inv__section--white">
      <div class="inv__shell">
        <div class="inv__section-header">
          <h2 class="inv__section-title">{{ $t('invest.pricingTitle') }}</h2>
          <p class="inv__section-sub">{{ $t('home.pricingSubtitle') }}</p>
          <div class="inv__divider"></div>
        </div>
        <div class="inv__pricing">
          <div
            v-for="(row, i) in pricingRows"
            :key="i"
            class="inv__pricing-row"
            :class="{ 'inv__pricing-row--featured': row.featured }"
          >
            <div class="inv__pricing-left">
              <strong>{{ $t(row.titleKey) }}</strong>
              <span>{{ $t(row.descKey) }}</span>
            </div>
            <div class="inv__pricing-right">
              <em>{{ row.value }}</em>
              <small>{{ $t(row.noteKey) }}</small>
            </div>
          </div>
        </div>

        <!-- Commitments -->
        <div class="inv__commit">
          <h3 class="inv__commit-title">{{ $t('invest.commitTitle') }}</h3>
          <div class="inv__commit-grid">
            <div v-for="(c, i) in commitments" :key="i" class="inv__commit-item">
              <div class="inv__commit-icon"><i :class="c.icon"></i></div>
              <div>
                <h4 class="inv__commit-item-title">{{ $t(c.titleKey) }}</h4>
                <p class="inv__commit-item-desc">{{ $t(c.descKey) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="inv__section inv__section--light">
      <div class="inv__shell inv__shell--narrow">
        <div class="inv__section-header">
          <h2 class="inv__section-title">{{ $t('invest.faqTitle') }}</h2>
          <div class="inv__divider"></div>
        </div>
        <div class="inv__faq">
          <div
            v-for="(faq, i) in faqs"
            :key="i"
            class="inv__faq-item"
            :class="{ 'is-open': activeFaq === i }"
            @click="toggleFaq(i)"
          >
            <div class="inv__faq-question">
              <span>{{ $t(faq.qKey) }}</span>
              <em class="inv__faq-icon" :class="{ open: activeFaq === i }">+</em>
            </div>
            <div v-if="activeFaq === i" class="inv__faq-answer">{{ $t(faq.aKey) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="inv__cta">
      <div class="inv__shell">
        <h2 class="inv__cta-title">{{ $t('invest.ctaTitle') }}</h2>
        <p class="inv__cta-desc">{{ $t('invest.ctaDesc') }}</p>
        <div class="inv__cta-actions">
          <button class="inv__btn inv__btn--primary" @click="goToContact">
            {{ $t('invest.ctaPrimary') }}
          </button>
          <a href="https://t.me/songhancapital" target="_blank" rel="noopener noreferrer" class="inv__btn inv__btn--outline">
            {{ $t('invest.ctaSecondary') }}
          </a>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./invest.js"></script>
<style src="./invest.scss" lang="scss"></style>
